<template>
  <div class="global-overview">
    <h3 class="overview-title text-uppercase">
      {{ $t('adminAccountManagement.overview.title') }}
    </h3>
    <div class="overview-grid">
      <div
        v-for="stat in stats"
        :key="stat.key"
        :class="['overview-card', stat.tone ? `tone-${stat.tone}` : '']"
      >
        <div class="overview-icon">
          <b-icon :icon="stat.icon" aria-hidden="true" scale="1.2"></b-icon>
        </div>
        <div class="overview-body">
          <div class="overview-value">{{ stat.value }}</div>
          <div class="overview-label">{{ stat.label }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import GetAdminGlobalOverview from '@/graphQlQueries/queries/getAdminGlobalOverview';

const TRIAL_DAYS = 14;
const DAY = 24 * 60 * 60 * 1000;

export default {
  name: 'AdminGlobalOverview',
  props: {
    flatGroups: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      overview: null,
    };
  },
  apollo: {
    overview: {
      query: GetAdminGlobalOverview,
      variables() {
        return {
          thirtyDaysAgo: new Date(Date.now() - 30 * DAY).toISOString(),
        };
      },
      update(data) {
        return data;
      },
      fetchPolicy: 'network-only',
    },
  },
  computed: {
    trialAccountsCount() {
      return this.flatGroups.filter((g) => g?.settings?.isTestAccount === true).length;
    },
    trialsExpiringThisWeek() {
      const now = Date.now();
      const oneWeek = 7 * DAY;
      return this.flatGroups.filter((g) => {
        if (g?.settings?.isTestAccount !== true) return false;
        if (!g.created_at) return false;
        const expires = new Date(g.created_at).getTime() + TRIAL_DAYS * DAY;
        const remaining = expires - now;
        return remaining > 0 && remaining <= oneWeek;
      }).length;
    },
    disabledAccountsCount() {
      return this.flatGroups.filter((g) => g?.settings?.disableAccount === true).length;
    },
    stats() {
      const get = (key) => this.overview?.[key]?.aggregate?.count ?? 0;
      return [
        {
          key: 'accounts',
          icon: 'building',
          value: get('totalAccounts'),
          label: this.$t('adminAccountManagement.overview.totalAccounts'),
        },
        {
          key: 'trials',
          icon: 'hourglass-split',
          value: this.trialAccountsCount,
          label: this.$t('adminAccountManagement.overview.trialAccounts'),
          tone: this.trialAccountsCount > 0 ? 'warn' : null,
        },
        {
          key: 'expiring',
          icon: 'alarm',
          value: this.trialsExpiringThisWeek,
          label: this.$t('adminAccountManagement.overview.trialsExpiringThisWeek'),
          tone: this.trialsExpiringThisWeek > 0 ? 'danger' : null,
        },
        {
          key: 'disabled',
          icon: 'slash-circle',
          value: this.disabledAccountsCount,
          label: this.$t('adminAccountManagement.overview.disabledAccounts'),
          tone: this.disabledAccountsCount > 0 ? 'danger' : null,
        },
        {
          key: 'activeUsers',
          icon: 'person-check',
          value: `${get('activeUsersLast30')} / ${get('totalUsers')}`,
          label: this.$t('adminAccountManagement.overview.activeUsersLast30'),
        },
        {
          key: 'publishedPages',
          icon: 'cloud-check',
          value: get('totalPublishedPages'),
          label: this.$t('adminAccountManagement.overview.publishedPages'),
        },
        {
          key: 'publishedProjects',
          icon: 'collection',
          value: get('totalPublishedProjects'),
          label: this.$t('adminAccountManagement.overview.publishedProjects'),
        },
        {
          key: 'pagesLast30',
          icon: 'graph-up',
          value: get('pagesPublishedLast30'),
          label: this.$t('adminAccountManagement.overview.pagesPublishedLast30'),
        },
      ];
    },
  },
};
</script>

<style scoped lang="scss">
.global-overview {
  background-color: rgba(0, 0, 0, 0.15);
  border-radius: 4px;
  padding: 16px 20px;
  margin-bottom: 16px;
}

.overview-title {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 1px;
  margin-bottom: 12px;
}

.overview-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 10px;
}

.overview-card {
  display: flex;
  align-items: center;
  background-color: #53575B;
  border-radius: 4px;
  padding: 10px 12px;
  box-shadow: 0 2px 3px 0 rgba(0, 0, 0, 0.16);
}

.overview-icon {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(83, 220, 134, 0.15);
  color: #53DC86;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  flex-shrink: 0;
}

.overview-body {
  min-width: 0;
}

.overview-value {
  color: #fff;
  font-size: 18px;
  font-weight: 700;
  line-height: 1.1;
}

.overview-label {
  color: #b9b9b9;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 2px;
}

.tone-warn .overview-icon {
  background-color: rgba(255, 193, 7, 0.18);
  color: #ffc107;
}

.tone-danger .overview-icon {
  background-color: rgba(220, 83, 83, 0.18);
  color: #DC5353;
}

.tone-danger .overview-value {
  color: #ff8a8a;
}
</style>
